/**
 * 강사 ag-grid 인라인 편집 지원
 * - applyTutorEditing: 기존 tutorsColDefs 트리에 editable/cellEditor/valueSetter(검증) 주입
 * - mapTutorRowToEditDTO: 그리드 행 → PATCH /tutor/{id} 요청 바디 매핑
 *
 * 편집 활성화는 grid context.editMode 로 제어(토글 시 refreshCells).
 */
import { message } from 'antd'
import { TUTOR_FIELD_SPEC, validateTutorField } from 'utils/validation/tutorEditSpec'

// 인라인 편집에서 제외(식별자/파티션키/배열 표시용 컬럼)
export const TUTOR_EDIT_EXCLUDED = new Set([
    'tutorId',
    'regionId',
    'registrationYear', // 활동연도 = 연도 파티션/필터 기준 → 인라인 변경 제외
    'createdAt',
])

const editableByMode = params => !!params.context?.editMode

/**
 * 컬럼 헤더 툴팁 — 편집 규칙과 주의사항을 마우스오버로 안내합니다.
 * 유효성 메시지는 어겼을 때만 보이므로, 미리 알 수 있게 헤더에 붙입니다.
 * (표시 여부·줄바꿈은 AgGrid 컴포넌트가 편집 모드에서만 처리합니다)
 */
const HEADER_NOTES = {
    // 편집 불가 사유
    tutorId: '식별자이므로 수정할 수 없습니다.',
    regionId: '지역은 수정할 수 없습니다.',
    registrationYear: '활동연도는 연도 필터의 기준이므로 수정할 수 없습니다.',
    createdAt: '등록일시는 자동으로 기록됩니다.',
    employmentStatus: '계약 시작일·만료일에 따라 자동으로 정해집니다.',

    // 편집 규칙
    isEmployment: '신규·재고용 중에서 선택합니다.',
    advancedInstructor: '초급강사·중급강사·고급강사 중에서 선택합니다.',
    tutorName: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다. 같은 지역·활동연도에 동명이인이 있으면 저장할 수 없습니다.',
    gender: '남·여 중에서 선택합니다.',
    residentNum: '숫자 6자리(YYMMDD)로 입력합니다. 실제 존재하는 날짜여야 합니다.',
    address: '2자 이상 입력합니다.',
    contact: '010-0000-0000 형식으로 입력합니다.',
    email: '@ 와 . 을 포함한 이메일 형식으로 입력합니다.',
    cohortStartYear: '선택 항목입니다. 4자리 연도로 입력합니다. (예: 2025)',
    startDate: 'YYYY-MM-DD 형식으로 입력합니다. 연도는 활동연도와 같아야 합니다.',
    endDate: 'YYYY-MM-DD 형식으로 입력합니다. 연도는 활동연도와 같아야 하며 시작일보다 빠를 수 없습니다.',
    employmentType: '직접채용·위촉 중에서 선택합니다.',
    major: '2자 이상 입력합니다.',
    educationLevel: '고졸·전문대졸·대졸·석사·박사 중에서 선택합니다.',
    privateCertification: '1급·2급·3급·없음 중에서 선택합니다.',
    languages: '선택 항목입니다. 입력할 경우 2자 이상이어야 합니다.',
    swCertification: '여러 개는 쉼표로 구분해 입력합니다.',
    areaName: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    cooperative: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    remarks: '선택 항목입니다. 형식 제한 없이 입력합니다.',

    // 체크박스(Y/N) — 클릭으로 바로 전환됩니다
    isYouth: '해당하면 체크합니다.',
    isRetiree: '해당하면 체크합니다.',
    isCareerGapFemale: '해당하면 체크합니다.',
    isForeign: '해당하면 체크합니다.',
    isDisability: '해당하면 체크합니다.',
    isVeterans: '해당하면 체크합니다.',
    isTeacherCertification: '해당하면 체크합니다.',
    isTeachingExperience: '해당하면 체크합니다.',
    isLanguage: '해당하면 체크합니다. 체크한 경우 사용 가능한 외국어를 입력합니다.',
    isCar: '해당하면 체크합니다.',
}

/** 헤더 툴팁 주입 (편집 여부와 무관하게 모든 컬럼에 적용) */
const withHeaderNote = col => {
    const note = HEADER_NOTES[col.field]
    return note ? { ...col, headerTooltip: note } : col
}

// 문자열 셀 공통 valueSetter: 검증 통과 시에만 반영
const makeTextValueSetter = key => params => {
    const raw = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue
    const row = { ...params.data, [key]: raw }
    const msg = validateTutorField(key, raw, row)
    if (msg) {
        message.error(msg)
        return false
    }
    params.data[key] = raw
    return true
}

// select 셀 valueSetter
const makeSelectValueSetter = key => params => {
    const val = params.newValue
    const msg = validateTutorField(key, val, { ...params.data, [key]: val })
    if (msg) {
        message.error(msg)
        return false
    }
    params.data[key] = val
    return true
}

/** 단일 컬럼에 편집 속성 주입 (해당되면 새 객체 반환, 아니면 원본) */
const decorateColumn = col => {
    if (!col || !col.field || TUTOR_EDIT_EXCLUDED.has(col.field)) return col

    const { field } = col

    // 고용 여부: 내부값은 boolean, 편집/표시는 신규/재고용 문자열
    if (field === 'isEmployment') {
        return {
            ...col,
            editable: editableByMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['신규', '재고용'] },
            valueGetter: p => (p.data ? (p.data.isEmployment ? '재고용' : '신규') : ''),
            valueSetter: p => {
                p.data.isEmployment = p.newValue === '재고용'
                return true
            },
            cellRenderer: p => p.value,
        }
    }

    // boolean 컬럼: ag-grid 기본 boolean 에디터(체크박스)
    if (col.cellDataType === 'boolean') {
        return { ...col, editable: editableByMode }
    }

    const spec = TUTOR_FIELD_SPEC[field]
    if (!spec) return col // 스펙에 없는 컬럼은 읽기 전용 유지

    if (spec.editor === 'select') {
        return {
            ...col,
            editable: editableByMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: spec.options },
            valueSetter: makeSelectValueSetter(field),
        }
    }

    // text 계열
    return {
        ...col,
        editable: editableByMode,
        cellEditor: 'agTextCellEditor',
        valueSetter: makeTextValueSetter(field),
    }
}

/** tutorsColDefs(그룹 포함) 전체에 편집 속성 + 헤더 툴팁 주입 */
export const applyTutorEditing = colDefs =>
    colDefs.map(col => {
        if (col.children) {
            return { ...col, children: col.children.map(c => withHeaderNote(decorateColumn(c))) }
        }
        return withHeaderNote(decorateColumn(col))
    })

/** 그리드 행 → PATCH /tutor/{id} 바디.
 *  - swCertification: 콤마 문자열 → 배열 (백엔드가 무조건 덮어쓰므로 현재값 보존 위해 필수)
 *  - isEmployment: boolean 그대로
 *  - employmentStatus/createdAt/swCertificationList 등 표시 전용 필드는 제외
 */
export const mapTutorRowToEditDTO = row => {
    const {
        employmentStatus, // 서버가 startDate/endDate로 재계산 → 전송 불필요
        createdAt,
        swCertificationList,
        swCertification,
        ...rest
    } = row

    const swArr =
        Array.isArray(swCertification) ? swCertification
        : swCertification ? String(swCertification).split(',').map(s => s.trim()).filter(Boolean)
        : []

    return {
        ...rest,
        swCertification: swArr,
    }
}
